import { useState, useEffect, useCallback } from 'react';
import api from '../../api/axios';
import BoilerCard from '../../components/BoilerCard';
import { useBoilerSocket } from '../../hooks/useSocket';
import EmptyState, { BoilerEmptyIcon, SearchEmptyIcon } from '../../components/EmptyState';

function BoilerSocketListener({ boilerId, onUpdate }) {
  useBoilerSocket(boilerId, onUpdate);
  return null;
}

export default function AdminBoilersPage() {
  const [boilers, setBoilers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ user_id: '', name: '', client_id: '', boiler_id: '', location: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    const [b, c] = await Promise.all([api.get('/boilers'), api.get('/users')]);
    setBoilers(b.data);
    setClients(c.data);
  };

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await api.post('/boilers', form);
      await load();
      setShowModal(false);
      setForm({ user_id: '', name: '', client_id: '', boiler_id: '', location: '' });
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка');
    } finally {
      setSaving(false);
    }
  };

  const handleBoilerUpdate = useCallback((data) => {
    setBoilers((prev) =>
      prev.map((boiler) =>
        boiler.id === data.boilerId
          ? { ...boiler, [data.field]: data.value, last_seen: data.timestamp }
          : boiler
      )
    );
  }, []);

  const filtered = boilers.filter((b) =>
    b.name.toLowerCase().includes(search.toLowerCase()) ||
    (b.owner_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (b.location || '').toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-white">Все котельные</h1>
          <p className="text-slate-400 text-sm mt-0.5">{boilers.length} устройств</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-xl transition-colors flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Добавить
        </button>
      </div>

      <div className="mb-4">
        <input
          type="text"
          placeholder="Поиск по названию, клиенту, адресу..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-blue-500"
        />
      </div>

      {boilers.map((b) => (
        <BoilerSocketListener key={b.id} boilerId={b.id} onUpdate={handleBoilerUpdate} />
      ))}

      {boilers.length === 0 ? (
        <EmptyState
          icon={<BoilerEmptyIcon />}
          title="Нет котельных"
          description="Нажмите кнопку выше, чтобы добавить первую котельную"
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<SearchEmptyIcon />}
          title="Ничего не найдено"
          description="Попробуйте изменить поисковый запрос"
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((boiler) => (
            <BoilerCard key={boiler.id} boiler={boiler} />
          ))}
        </div>
      )}

      {/* Add Boiler Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-md border border-slate-700">
            <h2 className="text-lg font-bold text-white mb-4">Добавить котельную</h2>
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg px-4 py-2 mb-4 text-sm">{error}</div>
            )}
            <form onSubmit={handleAdd} className="space-y-3">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Клиент</label>
                <select
                  value={form.user_id}
                  onChange={(e) => setForm((p) => ({ ...p, user_id: e.target.value }))}
                  className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                >
                  <option value="">Без клиента (тестовая)</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>{c.name} ({c.email || c.phone})</option>
                  ))}
                </select>
              </div>
              {[
                { key: 'name', label: 'Название', placeholder: 'Котельная №1' },
                { key: 'client_id', label: 'Client ID (MQTT)', placeholder: 'client_001' },
                { key: 'boiler_id', label: 'Boiler ID (MQTT)', placeholder: 'boiler_001' },
                { key: 'location', label: 'Адрес / Расположение', placeholder: 'ул. Ленина 1, кв. 5' },
              ].map(({ key, label, placeholder }) => (
                <div key={key}>
                  <label className="block text-xs text-slate-400 mb-1">{label}</label>
                  <input
                    type="text"
                    value={form[key]}
                    onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.value }))}
                    required={key !== 'location'}
                    placeholder={placeholder}
                    className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>
              ))}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setError(''); }}
                  className="flex-1 bg-slate-700 hover:bg-slate-600 text-white rounded-lg py-2.5 text-sm transition-colors"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg py-2.5 text-sm font-medium transition-colors"
                >
                  {saving ? 'Сохранение...' : 'Добавить'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
